import { useState, useEffect } from 'react';
import { geocodeAddress } from '../requests/geocoding';
import useQueryParam from './useQueryParam';
import { requestScriptLoad } from '@components/helpers/externalScriptHelper';
import { useInstantSearch } from 'react-instantsearch';
import { useSearchClient } from '@javascript/instant_search/provider/searchClientProvider';

type Location = {
  lat: string | undefined;
  lng: string | undefined;
};

export const useLocationData = () => {
  const [postcodeParam, updatePostcodeParam] = useQueryParam('location');
  const [location, setLocation] = useState<Location>({
    lat: undefined,
    lng: undefined,
  });
  const [isGoogleMapsLoaded, setIsGoogleMapsLoaded] = useState(false);
  const { setUiState } = useInstantSearch();
  const { indexName } = useSearchClient();

  useEffect(() => {
    if (isGoogleMapsLoaded) return;

    if (window.google) {
      setIsGoogleMapsLoaded(true);
      return;
    }

    if (!postcodeParam) return;

    const handleLoad = () => setIsGoogleMapsLoaded(true);

    requestScriptLoad('google', handleLoad);
  }, [postcodeParam]);

  useEffect(() => {
    const fetchLocationData = async () => {
      try {
        if (postcodeParam && isGoogleMapsLoaded) {
          const { lat, lng } = await geocodeAddress(postcodeParam);
          setLocation({
            lat: lat.toString(),
            lng: lng.toString(),
          });

          setUiState((prev) => ({
            ...prev,
            [indexName]: {
              ...prev[indexName],
              page: 0,
            },
          }));
        }
      } catch (error) {
        if (window.newrelic) {
          window.newrelic.noticeError(error);
        }
      }
    };

    if (isGoogleMapsLoaded) {
      fetchLocationData();
    }
  }, [postcodeParam, isGoogleMapsLoaded]);

  useEffect(() => {
    if (!postcodeParam) {
      setLocation({
        lat: undefined,
        lng: undefined,
      });
    }
  }, [postcodeParam]);

  return {
    location,
    postcodeParam,
    updatePostcodeParam,
  };
};
